import { useState } from 'react';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { AddPortfolio } from '@/components/Modals/Portfolio/AddPortfolio';
import { CarouselPortfolio } from '@/components/Portfolio/CarouselPortfolio';
import { useUser } from '@/contexts/AuthContext';
import MainContainer from '@/layouts/MainContainer';
import { PageLayout } from '@/layouts/PageLayout';
import { api } from '@/services/apiClient';
import { ICompany, IPortfolioItem } from '@/types/api/companies';
import { canEditCompanyInstitutionalPage } from '@/utils/components/institutionalPage';
import { CURRENT_ENVIRONMENT_URL, ROUTER_PAGES } from '@/utils/constants';
import { Check, Edit, Share } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

interface IPortfolioPageProps {
  company: ICompany;
  portfolioSlug: IPortfolioItem;
  previousUrl: string;
}

const PortfolioItem = ({
  company,
  portfolioSlug,
  previousUrl,
}: IPortfolioPageProps) => {
  const { isAuthenticated, user } = useUser();
  const router = useRouter();
  const [isCopied, setIsCopied] = useState(false);
  const [isOpenAddPortfolio, setIsOpenAddPortfolio] = useState(false);
  const [valuesItemPortfolio, setValuesItemPortfolio] =
    useState<IPortfolioItem>(portfolioSlug);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
  };

  return (
    <>
      <Head>
        <title>{portfolioSlug.title}</title>

        <meta
          name="application-name"
          content={portfolioSlug.title}
          key="application-name"
        />
      </Head>

      <NextSeo
        description={company.description}
        openGraph={{
          type: 'website',
          url: `${CURRENT_ENVIRONMENT_URL}/site/${company.slug}/portfolio/${portfolioSlug.slug}`,
          title: portfolioSlug.title,
          siteName: portfolioSlug.title,
          description: portfolioSlug.description,
          images: [
            {
              url:
                portfolioSlug.photos.length > 0
                  ? portfolioSlug.photos[0].photo
                  : company.logo,
              alt:
                portfolioSlug.photos.length > 0
                  ? portfolioSlug.title
                  : company.full_name,
              height: 320,
              width: 320,
            },
          ],
        }}
      />

      <PageLayout
        title="Portfólio"
        onBack={() =>
          previousUrl &&
          new URL(previousUrl.toString()).pathname ===
            `${ROUTER_PAGES.site}/${router.query.company_slug}`
            ? router.push(`${ROUTER_PAGES.site}/${router.query.company_slug}/`)
            : router.push(
                `${ROUTER_PAGES.site}/${router.query.company_slug}/portfolio/`
              )
        }
        removeMarginTop
      >
        <MainContainer
          component="main"
          sx={{
            backgroundColor: 'background.paper',
            padding: '16px',
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.1)',
            height: '100%',
            minHeight: '100vh',
            gap: '16px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            sx={theme => ({
              display: 'block',
              [theme.breakpoints.up('md')]: {
                display: 'none',
              },
            })}
            variant="h6"
            component="h1"
          >
            {valuesItemPortfolio.title}
          </Typography>
          <CarouselPortfolio
            altTitle={valuesItemPortfolio.title}
            imagesList={valuesItemPortfolio.photos}
            isPinned={valuesItemPortfolio.pinned}
            activeThumbs
          />
          <Typography
            sx={theme => ({
              display: 'none',
              [theme.breakpoints.up('md')]: {
                display: 'block',
                marginTop: 1,
              },
            })}
            variant="h6"
            component="h1"
          >
            {valuesItemPortfolio.title}
          </Typography>
          <Typography
            component="h2"
            variant="body1"
            color="rgba(0, 0, 0, 0.5)"
            sx={{ mb: 2 }}
          >
            {valuesItemPortfolio.description}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            {isAuthenticated &&
              canEditCompanyInstitutionalPage(company, user) && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Edit />}
                  sx={{ maxWidth: '380px' }}
                  onClick={() => setIsOpenAddPortfolio(true)}
                >
                  editar
                </Button>
              )}

            <Button
              fullWidth
              variant="contained"
              startIcon={isCopied ? <Check /> : <Share />}
              sx={{ maxWidth: '380px' }}
              onClick={handleCopyUrl}
            >
              {isCopied ? 'URL Copiada!' : 'Copiar URL'}
            </Button>
          </Box>
        </MainContainer>
      </PageLayout>
      <AddPortfolio
        isEditPortfolioItem={valuesItemPortfolio}
        setValuesPortfolioItem={value => value && setValuesItemPortfolio(value)}
        isOpen={isOpenAddPortfolio}
        setIsOpen={value => {
          setIsOpenAddPortfolio(value);
        }}
        companyId={company.id}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const redirectToHome = {
    redirect: {
      destination: '/home',
      permanent: false,
    },
  };

  const previousUrl = context.req.headers.referer;

  try {
    const { data: company } = await api.get<ICompany>(
      `api/companies/${context.query.company_slug}/`
    );

    const { data: portfolioSlug } = await api.get<IPortfolioItem[]>(
      `api/company_portfolio_items/${context.query.portfolio_slug}/`
    );

    return {
      props: { company, portfolioSlug, previousUrl: previousUrl || null },
    };
  } catch (error) {
    return redirectToHome;
  }
};

export default PortfolioItem;
